import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'

import { NAV_LINKS } from '@/constants/site'

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(`${href}/`)
}

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [menuOpen])

    const solid = scrolled || menuOpen

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow,backdrop-filter] duration-700 ease-out',
                solid ? 'bg-ink/85 shadow-soft backdrop-blur-md' : 'bg-linear-to-b from-ink/70 to-transparent'
            )}
        >
            <div className='mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10'>
                <Link
                    href='/'
                    className='group flex items-baseline gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4 focus-visible:ring-offset-ink'
                >
                    <span className='font-heading text-2xl tracking-tight text-parchment transition-colors duration-500 group-hover:text-gold'>
                        Rembrandt
                    </span>

                    <span className='hidden text-[0.65rem] uppercase tracking-[0.3em] text-parchment/60 sm:inline'>
                        van Rijn
                    </span>
                </Link>

                <nav aria-label='Main' className='hidden md:block'>
                    <ul className='flex items-center gap-10'>
                        {NAV_LINKS.map((link) => {
                            const active = isActive(router.pathname, link.href)

                            return (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'relative py-2 text-sm tracking-wide transition-colors duration-500 after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-center after:bg-gold after:shadow-glow after:transition-transform after:duration-500 focus-visible:outline-none focus-visible:text-gold',
                                            active
                                                ? 'text-gold after:scale-x-100'
                                                : 'text-parchment/75 after:scale-x-0 hover:text-parchment hover:after:scale-x-100'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <button
                    type='button'
                    aria-expanded={menuOpen}
                    aria-controls='mobile-menu'
                    onClick={() => setMenuOpen((open) => !open)}
                    className='-mr-2 inline-flex size-11 items-center justify-center rounded-sm text-parchment transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold md:hidden'
                >
                    {menuOpen ? <X className='size-6' aria-hidden='true' /> : <Menu className='size-6' aria-hidden='true' />}
                    <span className='sr-only'>{menuOpen ? 'Close menu' : 'Open menu'}</span>
                </button>
            </div>

            <nav
                id='mobile-menu'
                aria-label='Mobile'
                className={cn(
                    'overflow-hidden transition-[max-height,opacity] duration-500 ease-out md:hidden',
                    menuOpen ? 'max-h-96 opacity-100' : 'pointer-events-none max-h-0 opacity-0'
                )}
                inert={!menuOpen}
            >
                <ul className='space-y-1 px-6 pb-8 pt-2'>
                    {NAV_LINKS.map((link) => {
                        const active = isActive(router.pathname, link.href)

                        return (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'flex items-center justify-between rounded-sm px-3 py-3.5 font-heading text-2xl transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
                                        active ? 'bg-gold/10 text-gold' : 'text-parchment/85 hover:bg-parchment/5 hover:text-parchment'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </nav>
        </header>
    )
}

export default SiteHeader
